import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SiteContentContext = createContext();

export const useSiteContent = () => {
  const context = useContext(SiteContentContext);
  if (!context) {
    throw new Error('useSiteContent must be used within a SiteContentProvider');
  }
  return context;
};

export const SiteContentProvider = ({ children }) => {
  const [content, setContent] = useState({});
  const [elements, setElements] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchContent = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/site-content`);
      const data = await response.json();
      
      if (data.success) {
        const contentMap = {};
        data.content.forEach(item => {
          contentMap[item.key] = item.value;
        });
        setContent(contentMap);
      }
    } catch (error) {
      // Silently handle fetch errors - content will use defaults
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchElements = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/elements`);
      const data = await response.json();
      
      if (data.success) {
        setElements(data.elements);
      }
    } catch (error) {
      // Silently handle fetch errors - elements will be empty
    }
  }, []);

  useEffect(() => {
    fetchContent();
    fetchElements();
  }, [fetchContent, fetchElements]);

  const getContent = useCallback((key, defaultValue = '') => {
    return content[key] || defaultValue;
  }, [content]);

  const contextValue = useMemo(() => ({
    content,
    getContent,
    elements,
    isLoading,
    refetch: fetchContent,
    refetchElements: fetchElements
  }), [content, getContent, elements, isLoading, fetchContent, fetchElements]);

  return (
    <SiteContentContext.Provider value={contextValue}>
      {children}
    </SiteContentContext.Provider>
  );
};

export default SiteContentContext;
